import React, { useState, useEffect } from 'react';
import { TextField, MenuItem, CircularProgress } from '@mui/material';
import { squadApi } from '../../services/api';

interface SquadDropdownProps {
  value: number | null;
  onChange: (value: number | null) => void;
  seriesId?: number | null;
  matchId?: number | null;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

interface SquadOption {
  id: number;
  team: number;
  team_name: string;
  series: number;
}

const SquadDropdown: React.FC<SquadDropdownProps> = ({
  value,
  onChange,
  seriesId,
  matchId,
  error,
  helperText,
  disabled = false,
}) => {
  const [options, setOptions] = useState<SquadOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;

    const fetchSquads = async () => {
      if (!seriesId && !matchId) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        let response;
        
        if (matchId) {
          response = await squadApi.getAll({ match_id: matchId });
        } else if (seriesId) {
          response = await squadApi.getAll({ series: seriesId });
        } else {
          response = { data: { results: [] } };
        }
        
        if (active) {
          const data = response.data;
          setOptions(data.results || data); // Handle both paginated and non-paginated responses
        }
      } catch (error) {
        console.error('Error fetching squads:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSquads();

    return () => {
      active = false;
    };
  }, [seriesId, matchId]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onChange(value === '' ? null : Number(value));
  };

  return (
    <TextField
      select
      fullWidth
      label="Squad"
      value={value || ''}
      onChange={handleChange}
      error={error}
      helperText={helperText}
      disabled={disabled || loading || options.length === 0}
      size="small"
      InputProps={{
        endAdornment: loading ? <CircularProgress color="inherit" size={20} /> : null,
      }}
    >
      <MenuItem value="">
        <em>Select a squad</em>
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          {option.team_name}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default SquadDropdown;
